import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { Link } from "react-router-dom";
import { MEDIA } from "../data/media";

const PHASES = [
  {
    tag: "PHASE 01 // THE IDEA",
    img: MEDIA.spark,
    render: () => (
      <>
        <p className="mb-6 text-[10px] uppercase tracking-[0.35em] text-gold sm:text-xs" style={{ textShadow: "0 2px 14px rgba(0,0,0,0.9)" }}>Custom Business Platforms · Calgary</p>
        <h1 className="mx-auto max-w-4xl font-head text-4xl font-light leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl" style={{ textShadow: "0 3px 26px rgba(0,0,0,0.9)" }}>
          Most businesses adapt to software.<br className="hidden sm:block" /> <span className="text-gold italic">AberCal engineers platforms that adapt to your business.</span>
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-[var(--text)] sm:text-base" style={{ textShadow: "0 2px 16px rgba(0,0,0,0.9)" }}>
          Yours shouldn't have to. Replace spreadsheets, disconnected systems and expensive subscriptions with one platform designed specifically for the way your business works.
        </p>
        <p className="mt-5 font-head text-lg italic text-[var(--text-2)] sm:text-xl" style={{ textShadow: "0 2px 18px rgba(0,0,0,0.9)" }}>If you can dream it, I can build it.</p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
          <Link to="/contact" data-testid="hero-cta" className="btn-gold">Start a Conversation</Link>
          <Link to="/services" data-testid="hero-secondary" className="btn-ghost">See what I build</Link>
        </div>
      </>
    ),
  },
  {
    tag: "PHASE 02 // THE TANGLE",
    img: MEDIA.tangle,
    render: () => (
      <>
        <div className="mx-auto mb-10 max-w-xl space-y-1 text-sm leading-relaxed text-[var(--text-2)] sm:text-base">
          <p>Every business has ideas. Ideas to improve efficiency. Ideas to save time. Ideas to reduce costs.</p>
          <p>The challenge isn't having the idea. The challenge is bringing it to life.</p>
        </div>
        <p className="mb-6 text-xs uppercase tracking-[0.3em] text-gold">The Tangle</p>
        <h2 className="mx-auto max-w-3xl font-head text-4xl font-light leading-tight tracking-tight text-[var(--text)] sm:text-5xl lg:text-6xl">
          No two businesses operate the same way, so why should they all be forced to use the same software?
        </h2>
      </>
    ),
  },
  {
    tag: "PHASE 03 // THE WEAVE",
    img: MEDIA.weave,
    render: () => (
      <>
        <p className="mb-6 text-xs uppercase tracking-[0.3em] text-gold" style={{ textShadow: "0 2px 14px rgba(0,0,0,0.95)" }}>The Weave</p>
        <h2 className="font-head text-6xl font-light tracking-tight text-[var(--text)] sm:text-7xl lg:text-8xl" style={{ textShadow: "0 3px 24px rgba(0,0,0,0.9)" }}>$10,000<span className="text-gold">+</span></h2>
        <p className="mx-auto mt-6 max-w-2xl text-base font-normal leading-relaxed text-[var(--text)] sm:text-lg" style={{ textShadow: "0 2px 18px rgba(0,0,0,0.98), 0 1px 4px rgba(0,0,0,0.95)" }}>
          Businesses often spend more than $10,000 every year on disconnected subscriptions that still don't fully meet their needs. <span className="text-gold italic">Woven into one fabric, that ends.</span>
        </p>
      </>
    ),
  },
  {
    tag: "PHASE 04 // THE PLATFORM",
    img: MEDIA.forge,
    render: () => (
      <>
        <p className="mb-8 text-xs uppercase tracking-[0.3em] text-gold">One Platform. One Source of Truth.</p>
        <div className="space-y-2">
          <p className="font-head text-4xl font-light tracking-tight text-[var(--text)] sm:text-5xl lg:text-6xl">Reduce complexity.</p>
          <p className="font-head text-4xl font-light tracking-tight text-gold sm:text-5xl lg:text-6xl">Increase efficiency.</p>
          <p className="font-head text-4xl font-light tracking-tight text-[var(--text)] sm:text-5xl lg:text-6xl">Lower operating costs.</p>
        </div>
        <Link to="/contact" data-testid="journey-cta" className="btn-gold mt-12 inline-block">Start a Conversation</Link>
      </>
    ),
  },
];

export default function CinematicJourney() {
  const ref = useRef(null);
  const [phase, setPhase] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setPhase(v < 0.25 ? 0 : v < 0.5 ? 1 : v < 0.75 ? 2 : 3);
  });

  // image opacities (crossfade) + subtle parallax scale
  const stops = [
    useTransform(scrollYProgress, [0, 0.18, 0.25], [1, 1, 0]),
    useTransform(scrollYProgress, [0.22, 0.3, 0.43, 0.5], [0, 1, 1, 0]),
    useTransform(scrollYProgress, [0.47, 0.55, 0.68, 0.75], [0, 1, 1, 0]),
    useTransform(scrollYProgress, [0.72, 0.82, 1], [0, 1, 1]),
  ];
  const scales = [
    useTransform(scrollYProgress, [0, 0.25], [1.15, 1.3]),
    useTransform(scrollYProgress, [0.25, 0.5], [1.1, 1.2]),
    useTransform(scrollYProgress, [0.5, 0.75], [1.15, 1.05]),
    useTransform(scrollYProgress, [0.75, 1], [1.2, 1.05]),
  ];
  const textStops = [
    useTransform(scrollYProgress, [0, 0.16, 0.24], [1, 1, 0]),
    useTransform(scrollYProgress, [0.24, 0.32, 0.42, 0.49], [0, 1, 1, 0]),
    useTransform(scrollYProgress, [0.49, 0.57, 0.67, 0.74], [0, 1, 1, 0]),
    useTransform(scrollYProgress, [0.74, 0.84, 1], [0, 1, 1]),
  ];

  return (
    <section ref={ref} data-testid="cinematic-journey" className="relative h-[420vh]">
      <div className="sticky top-0 h-screen overflow-hidden">
        {PHASES.map((ph, i) => (
          ph.tartan ? (
            <motion.div key={i} aria-hidden style={{ opacity: stops[i], scale: scales[i] }}
              className="tartan-scene absolute inset-0 h-full w-full will-change-transform" />
          ) : (
            <motion.img key={i} src={ph.img} alt={ph.tag} aria-hidden
              style={{ opacity: stops[i], scale: scales[i] }}
              className="absolute inset-0 h-full w-full object-cover will-change-transform" />
          )
        ))}
        <div className="absolute inset-0 bg-black/45" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 40%, rgba(5,5,5,0.65) 100%)" }} />

        {/* HUD */}
        <div data-testid="journey-hud" className="absolute left-6 top-1/2 z-20 -translate-y-1/2 sm:left-10">
          <div className="tartan-line mb-4 w-14" />
          {PHASES.map((ph, i) => (
            <div key={i} data-testid={`hud-phase-${i}`} className="my-2 text-[10px] tracking-[0.3em] transition-all duration-500"
              style={{ color: phase === i ? "var(--gold)" : "var(--muted)", opacity: phase === i ? 1 : 0.5 }}>
              {ph.tag}
            </div>
          ))}
        </div>

        {/* Pinned text per phase */}
        <div className="absolute inset-0 z-10 flex items-center justify-center px-6">
          <div className="relative w-full max-w-4xl text-center">
            {PHASES.map((ph, i) => (
              <motion.div key={i} style={{ opacity: textStops[i], pointerEvents: phase === i ? "auto" : "none" }}
                className="absolute inset-x-0 top-1/2 -translate-y-1/2">
                {ph.render()}
              </motion.div>
            ))}
          </div>
        </div>

        {/* scroll hint */}
        <motion.div style={{ opacity: stops[0] }} className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-2)]">Scroll to weave the story</p>
          <div className="mx-auto mt-3 h-8 w-px animate-pulse bg-gradient-to-b from-[var(--gold)] to-transparent" />
        </motion.div>
      </div>
    </section>
  );
}
